/**
 * Service Error Handling
 * 
 * Standardized error types and handling for all service layer operations
 */

/**
 * Custom error class for service layer errors
 */
export class ServiceError extends Error {
  constructor(
    message: string,
    public code: ServiceErrorCode,
    public recoverable: boolean = true,
    public originalError?: unknown,
    public context?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ServiceError';
  }

  /**
   * Create a user-friendly error message
   */
  getUserMessage(): string {
    switch (this.code) {
      case 'NOT_CONFIGURED':
        return 'The service is not configured. Please contact an administrator.';
      case 'NOT_FOUND':
        return 'The requested resource was not found.';
      case 'UNAUTHORIZED':
        return 'You do not have permission to perform this action.';
      case 'VALIDATION_ERROR':
        return this.message;
      case 'NETWORK_ERROR':
        return 'Unable to connect. Please check your internet connection.';
      case 'RATE_LIMITED':
        return 'Too many requests. Please wait a moment and try again.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
}

/**
 * Service error codes
 */
export type ServiceErrorCode =
  | 'NOT_CONFIGURED'
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'VALIDATION_ERROR'
  | 'NETWORK_ERROR'
  | 'RATE_LIMITED'
  | 'DATABASE_ERROR'
  | 'STORAGE_ERROR'
  | 'UNKNOWN_ERROR';

/**
 * Standard service response type
 */
export type ServiceResponse<T> = {
  data: T;
  error: null;
  success: true;
} | {
  data: null;
  error: ServiceError;
  success: false;
};

/**
 * Create a successful response
 */
export const successResponse = <T>(data: T): ServiceResponse<T> => ({
  data,
  error: null,
  success: true,
});

/**
 * Create an error response
 */
export const errorResponse = <T>(error: ServiceError): ServiceResponse<T> => ({
  data: null,
  error,
  success: false,
});

/**
 * Handle and transform service errors consistently
 */
export const handleServiceError = (
  error: unknown,
  context: string,
  additionalContext?: Record<string, unknown>
): ServiceError => {
  // Already a ServiceError
  if (error instanceof ServiceError) {
    return error;
  }

  // Supabase error
  if (isSupabaseError(error)) {
    const supabaseError = error as SupabaseError;
    
    // Map Supabase error codes to our error codes
    let code: ServiceErrorCode = 'DATABASE_ERROR';
    let message = supabaseError.message;
    const recoverable = true;

    if (supabaseError.code === 'PGRST116') {
      code = 'NOT_FOUND';
      message = 'Resource not found';
    } else if (supabaseError.code === '42501' || supabaseError.message?.includes('permission')) {
      code = 'UNAUTHORIZED';
      message = 'Permission denied';
    } else if (supabaseError.code === '23505') {
      code = 'VALIDATION_ERROR';
      message = 'A record with this value already exists';
    } else if (supabaseError.code === '23503') {
      code = 'VALIDATION_ERROR';
      message = 'Referenced record does not exist';
    }

    console.error(`[${context}] Supabase error:`, supabaseError);
    
    return new ServiceError(message, code, recoverable, error, additionalContext);
  }

  // Network error
  if (error instanceof TypeError && error.message.includes('fetch')) {
    console.error(`[${context}] Network error:`, error);
    return new ServiceError(
      'Network request failed',
      'NETWORK_ERROR',
      true,
      error,
      additionalContext
    );
  }

  // Standard Error
  if (error instanceof Error) {
    console.error(`[${context}] Error:`, error);
    return new ServiceError(
      error.message,
      'UNKNOWN_ERROR',
      false,
      error,
      additionalContext
    );
  }

  // Unknown error type
  console.error(`[${context}] Unknown error:`, error);
  return new ServiceError(
    'An unexpected error occurred',
    'UNKNOWN_ERROR',
    false,
    error,
    additionalContext
  );
};

/**
 * Check if error is a Supabase error
 */
interface SupabaseError {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
}

const isSupabaseError = (error: unknown): error is SupabaseError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as SupabaseError).message === 'string'
  );
};

/**
 * Create a not configured error
 */
export const notConfiguredError = (service: string): ServiceError => {
  return new ServiceError(
    `${service} is not configured`,
    'NOT_CONFIGURED',
    false
  );
};

/**
 * Create a not found error
 */
export const notFoundError = (resource: string, id?: string): ServiceError => {
  const message = id ? `${resource} with ID '${id}' not found` : `${resource} not found`;
  return new ServiceError(message, 'NOT_FOUND', true);
};

/**
 * Create a validation error
 */
export const validationError = (message: string): ServiceError => {
  return new ServiceError(message, 'VALIDATION_ERROR', true);
};

/**
 * Wrap async function with error handling
 */
export const withErrorHandling = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: string
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
  return async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    try {
      return await fn(...args);
    } catch (error) {
      throw handleServiceError(error, context);
    }
  };
};

